import { trackPress } from './press';
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** The API's machine-readable reason, when it gives one (`{ code: 'CONFIRM' }`). */
    public code?: string,
    /** The whole answer, for errors that carry more than a sentence. */
    public data?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  // A button press that starts a request shows as busy until it settles (see lib/press.ts).
  return trackPress(request<T>(path, init));
}

async function request<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const req: RequestInit = {
    method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
    credentials: 'same-origin',
  };
  if (init.body !== undefined) {
    req.headers = { 'content-type': 'application/json' };
    req.body = JSON.stringify(init.body);
  }
  const res = await fetch(`/api/admin${path}`, req);
  const data = (await res.json().catch(() => ({}))) as { error?: unknown; code?: unknown };
  if (!res.ok)
    throw new ApiError(
      res.status,
      errorText(data.error, res.status),
      typeof data.code === 'string' ? data.code : undefined,
      data,
    );
  return data as T;
}

export const usd = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
export const coins = (n: number) => n.toLocaleString('en-US');
export const when = (t: number | null | undefined) => (t ? new Date(t).toLocaleString() : '—');
export function ago(t: number): string {
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/** The API answers `{ error: "sentence" }`; anything else (an object, nothing) still becomes readable text. */
function errorText(e: unknown, status: number): string {
  if (typeof e === 'string' && e) return e;
  if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
    try {
      const issues = JSON.parse(e.message) as { path?: string[]; message?: string }[];
      if (Array.isArray(issues) && issues[0])
        return `${issues[0].path?.join('.') ?? ''}: ${issues[0].message ?? 'invalid'}`;
    } catch {
      return e.message;
    }
  }
  return `Request failed (${status})`;
}
